// 화면 쪽 엔진 연결. 모드:
//   remote — PC 에서 http://127.0.0.1:8765 로 열 때: 서버 API 를 바로 부른다
//   device — 폰 등 다른 주소로 열 때: 기기 안의 엔진(worker)이 같은 API 를 처리하고, PC 와는 동기화
// 주소에 ?mode=device / ?mode=remote 를 붙이면 그 모드로 고정된다 (시험용).
import { useSyncExternalStore } from 'react';
import type { EngineResponse, EngineStatus, FromEngine, SyncInfo, ToEngine } from './protocol';

export type Mode = 'remote' | 'device';

function detectMode(): Mode {
  const q = new URLSearchParams(location.search).get('mode');
  if (q === 'device' || q === 'remote') {
    try {
      localStorage.setItem('apex-mode', q);
    } catch {
      // 저장 못 해도 이번에는 그 모드로
    }
    return q;
  }
  try {
    const saved = localStorage.getItem('apex-mode');
    if (saved === 'device' || saved === 'remote') return saved;
  } catch {
    // 저장소를 못 쓰면 주소로 판단
  }
  return ['127.0.0.1', 'localhost'].includes(location.hostname) ? 'remote' : 'device';
}

export const mode: Mode = detectMode();
export const isDevice = mode === 'device';

export interface EngineState { engine: EngineStatus; message: string; sync: SyncInfo }

let state: EngineState = {
  engine: isDevice ? 'loading' : 'off',
  message: '',
  sync: { state: 'idle', pending: 0, lastSync: null, conflicts: 0, error: null },
};
const listeners = new Set<() => void>();
const waiting = new Map<number, { resolve: (r: EngineResponse) => void; reject: (e: Error) => void }>();
let worker: Worker | null = null;
let seq = 0;

function update(patch: Partial<EngineState>): void {
  state = { ...state, ...patch };
  listeners.forEach((l) => l());
}

const send = (m: ToEngine) => worker?.postMessage(m);

export function startEngine(): void {
  if (!isDevice || worker) return;
  worker = new Worker(new URL('./engine.js', location.href), { type: 'module' });
  worker.onmessage = (e: MessageEvent<FromEngine>) => {
    const m = e.data;
    if (m.type === 'engine') {
      update({ engine: m.state, message: m.message ?? '' });
      if (m.state === 'ready') {
        void warmOfflineCache();
        send({ type: 'sync' });
      }
    } else if (m.type === 'sync') {
      update({ sync: m.info });
      if (m.info.changed) window.dispatchEvent(new Event('apex:data-changed'));
    } else if (m.type === 'sync-done') {
      syncWaiters.get(m.id)?.(m.info);
      syncWaiters.delete(m.id);
    } else if (m.type === 'response') {
      const w = waiting.get(m.id);
      waiting.delete(m.id);
      if (m.ok) w?.resolve(m.res);
      else w?.reject(new Error(m.error));
    } else if (m.type === 'reset-done') {
      location.reload();
    }
  };
  worker.onerror = (e) => update({ engine: 'error', message: e.message || '엔진 오류' });
}

export function engineRequest(method: string, path: string, body: string | null): Promise<EngineResponse> {
  if (!worker) startEngine();
  return new Promise((resolve, reject) => {
    const id = ++seq;
    waiting.set(id, { resolve, reject });
    send({ type: 'request', id, method, path, body });
  });
}

export const requestSync = (): void => send({ type: 'sync' });

const syncWaiters = new Map<number, (info: SyncInfo) => void>();

/** 지금 동기화하고, 이 요청 뒤에 시작된 동기화가 끝날 때까지 기다린다. */
export function syncAndWait(): Promise<SyncInfo> {
  if (!worker) startEngine();
  return new Promise((resolve) => {
    const id = ++seq;
    syncWaiters.set(id, resolve);
    send({ type: 'sync', id });
  });
}
export const resetDevice = (): void => send({ type: 'reset' });

export function useEngine(): EngineState {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => state,
  );
}

/** 엔진 파일(약 15MB)을 오프라인용 캐시에 넣어 둔다 — 서비스 워커가 처음 제어하기 전에 받은 파일도 빠짐없이. */
async function warmOfflineCache(): Promise<void> {
  if (!('caches' in window)) return;
  try {
    const manifest = (await (await fetch(new URL('pyodide/manifest.json', location.href))).json()) as { pyodide: string; wheels: string[] };
    const cache = await caches.open(`apex-pyodide-${manifest.pyodide}`);
    const files = ['pyodide.mjs', 'pyodide.asm.mjs', 'pyodide.asm.wasm', 'python_stdlib.zip', 'pyodide-lock.json', ...manifest.wheels.map((w) => `wheels/${w}`)];
    for (const f of files) {
      const url = new URL(`pyodide/${f}`, location.href).href;
      if (!(await cache.match(url))) await cache.add(url);
    }
  } catch {
    // 다음에 다시 시도
  }
}
